import React, { useMemo, useState } from 'react';
import { eachDayOfInterval, endOfMonth, endOfWeek, format, isSameMonth, isWeekend, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { Employee, PtoRequest } from '../../types/hr';
import type { Holiday } from '../../types/selfService';
import { displayName } from '../../utils/employees';
import { TODAY } from '../../utils/format';
import { holidayMap } from '../../utils/holidays';
import { isOutOn } from '../../utils/timeOff';
export type CalendarMode = 'month' | 'week';
interface TeamCalendarProps {
  anchor: Date;
  mode: CalendarMode;
  people: Employee[];
  holidays: Holiday[];
  requests: PtoRequest[];
  showPending: boolean;
  highlightId?: string;
}
interface DayItem {
  key: string;
  kind: 'holiday' | 'out' | 'pending' | 'anniversary';
  label: string;
}
const ITEM_STYLES: Record<DayItem['kind'], string> = {
  holiday: 'bg-flame/15 text-[#b45300]',
  out: 'bg-azure text-white',
  pending: 'border border-dashed border-flame/60 bg-white text-[#c25a00]',
  anniversary: 'bg-deep/10 text-deep'
};
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
export function TeamCalendar({
  anchor,
  mode,
  people,
  holidays,
  requests,
  showPending,
  highlightId
}: TeamCalendarProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const map = holidayMap(holidays);
  const days = mode === 'month' ? eachDayOfInterval({
    start: startOfWeek(startOfMonth(anchor)),
    end: endOfWeek(endOfMonth(anchor))
  }) : eachDayOfInterval({
    start: startOfWeek(anchor),
    end: endOfWeek(anchor)
  });
  const ranges = useMemo(() => {
    const ids = new Set(people.map(p => p.id));
    return [...timeOffEntries.filter(e => ids.has(e.employeeId)).map(e => ({
      id: e.id,
      employeeId: e.employeeId,
      start: e.start,
      end: e.end,
      pending: false
    })), ...requests.filter(r => ids.has(r.employeeId) && (r.status === 'approved' || showPending && r.status === 'pending')).map(r => ({
      id: r.id,
      employeeId: r.employeeId,
      start: r.start,
      end: r.end,
      pending: r.status === 'pending'
    }))];
  }, [people, requests, showPending]);
  const itemsFor = (d: Date): DayItem[] => {
    const iso = format(d, 'yyyy-MM-dd');
    const items: DayItem[] = [];
    const h = map.get(iso);
    if (h) items.push({
      key: h.id,
      kind: 'holiday',
      label: `${h.name}${h.halfDay ? ' · ½ day' : ''}`
    });
    if (!isWeekend(d) && !(h && !h.halfDay)) {
      ranges.filter(r => isOutOn(r, d)).forEach(r => {
        const person = people.find(p => p.id === r.employeeId);
        if (person) items.push({
          key: `${r.id}-${iso}`,
          kind: r.pending ? 'pending' : 'out',
          label: `${displayName(person)}${person.id === highlightId ? ' (you)' : ''}`
        });
      });
    }
    people.forEach(p => {
      const hire = parseISO(p.hireDate);
      const years = d.getFullYear() - hire.getFullYear();
      if (years >= 1 && hire.getMonth() === d.getMonth() && hire.getDate() === d.getDate()) {
        items.push({
          key: `ann-${p.id}`,
          kind: 'anniversary',
          label: `${displayName(p)} · ${years} yr`
        });
      }
    });
    return items;
  };
  const limit = mode === 'month' ? 3 : 12;
  const selectedDate = selected ? parseISO(selected) : null;
  const selectedItems = selectedDate ? itemsFor(selectedDate) : [];
  return <div data-id="element-703" data-mp="1bing4f83gbgb">
      <div className="grid grid-cols-7 border-l border-t border-slate-100" data-id="element-704" data-mp="16hpyq4uwrlfv">
        {days.slice(0, 7).map(d => <div key={`h-${d.toISOString()}`} className="border-b border-r border-slate-100 bg-canvas/70 px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-705" data-mp="1ki0qda41l423">
            {format(d, mode === 'week' ? 'EEE d' : 'EEE')}
          </div>)}
        {days.map(d => {
        const iso = format(d, 'yyyy-MM-dd');
        const outside = mode === 'month' && !isSameMonth(d, anchor);
        const items = itemsFor(d);
        const extra = items.length - limit;
        return <button key={iso} type="button" onClick={() => setSelected(iso)} aria-label={`${format(d, 'EEEE, MMMM d')}: ${items.length} ${items.length === 1 ? 'item' : 'items'}`} className={`flex flex-col gap-1 border-b border-r border-slate-100 p-1.5 text-left align-top transition hover:bg-azure/[0.04] ${mode === 'month' ? 'min-h-[112px]' : 'min-h-[320px]'} ${outside ? 'bg-canvas/50' : isWeekend(d) ? 'bg-canvas/30' : 'bg-white'} ${selected === iso ? 'ring-2 ring-inset ring-azure' : ''}`} data-id="element-706" data-mp="3om7m368sp1te">
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${iso === TODAY_ISO ? 'bg-flame text-white' : outside ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-707" data-mp="3eqxkgjmb6wg5">
                {format(d, 'd')}
              </span>
              {items.slice(0, limit).map(it => <span key={it.key} className={`truncate rounded-md px-1.5 py-0.5 text-[11px] font-medium ${ITEM_STYLES[it.kind]}`} data-id="element-708" data-mp="29q69ifoba692">
                  {it.label}
                </span>)}
              {extra > 0 && <span className="px-1.5 text-[11px] font-medium text-azure" data-id="element-709" data-mp="20ozukb1b93hx">+{extra} more</span>}
            </button>;
      })}
      </div>

      <AnimatePresence data-id="element-710" data-mp="1qzye6jqusk3f">
        {selectedDate && <motion.div initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 6
      }} transition={{
        duration: 0.15
      }} className="mt-4 rounded-2xl bg-canvas p-4" data-id="element-711" data-mp="1qz6kxfkqaygb">
            <div className="flex items-center justify-between" data-id="element-712" data-mp="1czxbcyr132lb">
              <p className="text-sm font-semibold text-navy" data-id="element-713" data-mp="1d29rd6d872vq">{format(selectedDate, 'EEEE, MMMM d')}</p>
              <button type="button" onClick={() => setSelected(null)} aria-label="Close day details" className="rounded-lg p-1 text-muted hover:bg-white hover:text-navy" data-id="element-714" data-mp="1836wgeqymd1h">
                <XIcon className="h-4 w-4" data-id="element-715" data-mp="12gmy45bb620t" />
              </button>
            </div>
            {selectedItems.length === 0 ? <p className="mt-2 text-sm text-muted" data-id="element-716" data-mp="ohn7is48jd20">Nothing scheduled — everyone's in.</p> : <ul className="mt-3 flex flex-wrap gap-2" data-id="element-717" data-mp="1h3zl7ygrkftb">
                {selectedItems.map(it => <li key={it.key} className={`rounded-lg px-2.5 py-1 text-xs font-medium ${ITEM_STYLES[it.kind]}`} data-id="element-718" data-mp="1m32opeh3idz6">
                    {it.kind === 'pending' ? `${it.label} · pending` : it.label}
                  </li>)}
              </ul>}
          </motion.div>}
      </AnimatePresence>

      <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted" data-id="element-719" data-mp="202k7nd7pmx0m">
        <li className="flex items-center gap-1.5" data-id="element-720" data-mp="24tag4zbfnfzj">
          <span className="h-3 w-3 rounded bg-azure" aria-hidden="true" data-id="element-721" data-mp="24v53sle39m7b" /> Out
        </li>
        {showPending && <li className="flex items-center gap-1.5" data-id="element-722" data-mp="3phux9htyvrty">
            <span className="h-3 w-3 rounded border border-dashed border-flame/60" aria-hidden="true" data-id="element-723" data-mp="3kjtgx4nxe6m6" /> Pending
          </li>}
        <li className="flex items-center gap-1.5" data-id="element-724" data-mp="263t693s2kca">
          <span className="h-3 w-3 rounded bg-flame/15" aria-hidden="true" data-id="element-725" data-mp="3uafp4dtbbuuu" /> Holiday
        </li>
        <li className="flex items-center gap-1.5" data-id="element-726" data-mp="36klgk8g10sge">
          <span className="h-3 w-3 rounded bg-deep/10" aria-hidden="true" data-id="element-727" data-mp="36n7le0bj8tux" /> Work anniversary
        </li>
      </ul>
    </div>;
}